<script setup lang="ts">
import {inject, ref, watchEffect} from 'vue';
import {formatting, LegacyOrder, legacyOrderKey, useLocalization} from '@hantera/portal-app';

import Delivery from './Delivery.vue'
import Rma from './Rma/Rma.vue'
import TimelineActivityLog from './TimelineActivityLog.vue'
import PaymentDetails from '@/components/LegacyPaymentDetails/LegacyPaymentDetails.vue';
import TimelineOrderStateChange from './TimelineOrderStateChange.vue';
import TimelineDeliveryStateChange from './TimelineDeliveryStateChange.vue';
import TimelineRmaStateChange from './TimelineRmaStateChange.vue';

interface TimelineEntry {
  id: string
  timestamp: Date
  is: any
  props: any
}

interface TimelineDate {
  date: Date
  entries: TimelineEntry[]
}

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const { date, time } = formatting;
const t = useLocalization();
const timeline = ref<TimelineDate[]>([]);

watchEffect(() => {
  const index: {
    [date: number]: {
      date: Date
      entries: TimelineEntry[]
    }
  } = {}

  order.value?.stateTransitions.forEach(s => {
    addEntry({
      id: `order-state${s.fromState}${s.toState}${s.timestampUtc.getTime()}`,
      timestamp: s.timestampUtc,
      is: TimelineOrderStateChange,
      props: {
        data: s
      }
    });
  });

  order.value?.deliveries.forEach(d => {
    addEntry({
      id: d.deliveryId,
      timestamp: d.createdUtc,
      is: Delivery,
      props: {
        data: d
      }
    });

    d.stateTransitions.forEach(s => {
      addEntry({
        id: `delivery-state${s.fromState}${s.toState}${s.timestampUtc.getTime()}`,
        timestamp: s.timestampUtc,
        is: TimelineDeliveryStateChange,
        props: {
          data: {
            delivery: d,
            transition: s,
          },
        },
      });
    });
  });

  order.value?.rmas.forEach(r => {
    addEntry({
      id: r.rmaId,
      timestamp: r.createdUtc,
      is: Rma,
      props: {
        data: r,
      }
    });

    r.stateTransitions.forEach(s => {
      addEntry({
        id: `rma-state${s.fromState}${s.toState}${s.timestampUtc.getTime()}`,
        timestamp: s.timestampUtc,
        is: TimelineRmaStateChange,
        props: {
          rma: r,
          transition: s,
        },
      });
    });
  });

  order.value?.payments.forEach(p => {
    addEntry({
      id: p.paymentId,
      timestamp: p.paymentDateUtc,
      is: PaymentDetails,
      props: {
        data: p,
      },
    });
  });

  order.value?.activityLogs?.forEach(d => {
    addEntry({
      id: "al" + hashString(JSON.stringify(d)).toString(),
      timestamp: d.createdUtc,
      is: TimelineActivityLog,
      props: d,
    });
  });

  timeline.value = Object.values(index);

  timeline.value.sort((a, b) => b.date.getTime() - a.date.getTime());
  timeline.value.forEach(d => d.entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()))


  function addEntry(entry: TimelineEntry) {
    let date: Date;
    if (entry.timestamp.getFullYear() === 1) {
      // HACK: It seems sometimes payment created date is not set properly. Likely a bug in Hantera/Litium.
      date = new Date(order.value.createdUtc.getFullYear(), order.value.createdUtc.getMonth(), order.value.createdUtc.getDate());
      entry.timestamp = order.value.createdUtc;
    } else {
      date = new Date(entry.timestamp.getFullYear(), entry.timestamp.getMonth(), entry.timestamp.getDate());
    }
    const key = date.getTime();
    let timelineDate = index[key];

    if (!timelineDate) {
      timelineDate = {
        date: date,
        entries: [entry]
      };
      index[key] = timelineDate;
    } else {
      timelineDate.entries.push(entry);
    }
  }
});

function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) + hash) + char; /* hash * 33 + char */
  }
  return hash;
}

</script>

<template>
  <div class="timeline">
    <template
      v-for="timelineDate in timeline"
      :key="timelineDate.date.getTime()"
    >
      <div class="timeline__date-header">
        {{ date(timelineDate.date) }}, {{ t(`common.weekdays.${timelineDate.date.getDay()}`) }}
      </div>
      <template
        v-for="entry in timelineDate.entries"
        :key="timelineDate.date.getTime() + entry.id"
      >
        <div class="timeline__time">
          {{ time(entry.timestamp) }}
          <div
            v-if="entry.props.userId && !entry.props.userId.startsWith('00000000')"
            class="timeline__user"
          >
            {{ entry.props.user }}
          </div>
        </div>
        <div class="timeline__entry">
          <component
            :is="entry.is"
            v-bind="entry.props"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.timeline {
  display: grid;
  grid-template-columns: minmax(10%, max-content) auto;
  gap: 8px;
}

.timeline__date-header {
  grid-column: 1 / 3;
  font-weight: bold;
  background-color: hsl(var(--color-90));
  line-height: 30px;
  padding-left: 5px;
}

.timeline__time {
  padding-left: 5px;
  text-align: right;
}

.timeline__entry {
  padding-right: 5px;
}

.timeline__user {
  opacity: .6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@container (max-width: 550px) {
  .timeline {
    display: flex;
    flex-direction: column;
  }
  .timeline__time {
    text-align: left;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 5px;
  }
  .timeline__user {
    display: inline-block;
  }
  .timeline__entry {
    padding: 0 5px 0 15px;
  }
  
}
</style>